import _ from 'lodash';
import { createSelector } from 'reselect';
import { Store } from '@/core/flux.service';
import {
  findDatasourceNamesAndIds,
  loadHierarchySearchOptions,
  loadStoredTableColumns,
} from '@/itemFinder/itemFinder.actions';
import { DEFAULT_STORED_TABLE_SIMPLE_SEARCH_TYPES, HierarchySearchMode } from '@/itemFinder/itemFinder.constants';
import {
  SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE,
  createDefaultStoredTableNameProperty,
  isStoredTableSimpleSearch,
} from '@/itemFinder/itemFinder.utilities';
import { OperatorEnum, type PropertyFilterInputV1 } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SearchTypeEnum } from '@seeqdev/api/model/PropertySearchV1';
import { SeeqNames } from '@seeqdev/constants';
import { base64guid } from '@seeqdev/utils';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { PersistenceLevel } from '@/core/flux.service';
import type {
  AncestorPredicate,
  HierarchyLevelColumn,
  Property,
  Search,
  SearchType,
  Value,
} from '@/itemFinder/itemFinder.constants';
import type { LoadedSearchesStorePolicy } from '@/itemFinder/itemFinder.types';
import type { SearchPropertyOption } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FixedListFilterInputV1 } from '@seeqdev/api/model/FixedListFilterInputV1';
import type { ItemPreviewV1 } from '@seeqdev/api/model/ItemPreviewV1';

const DEFAULT_FIXED_LIST_SEARCH = {
  isInclude: true,
  itemIds: [],
  searchType: '' as SearchType,
};

export type PropertiesList = {
  operator: OperatorEnum;
  propertyName: '';
  values: [{ value: string | number }];
  searchId: string;
  propertyId: string;
};

export type UpdateSearchPayload = {
  searchId: string | number | undefined;
  index?: number;
  action:
    | 'UPDATE_SEARCH_FIELDS'
    | 'ADD_PROPERTY'
    | 'REMOVE_PROPERTY'
    | 'UPDATE_PROPERTY'
    | 'ADD_VALUE'
    | 'REMOVE_VALUE'
    | 'UPDATE_VALUE'
    | 'UPDATE_STORED_TABLE_COLUMN'
    | 'ADD_DEFAULT_STORED_TABLE_SEARCH'
    | 'ADD_ANCESTOR_PREDICATE'
    | 'REMOVE_ANCESTOR_PREDICATE'
    | 'UPDATE_ANCESTOR_PREDICATE';
  propertyId?: string;
  propertyData?: Partial<{
    operator: OperatorEnum;
    propertyName: string;
    values?: Value[];
    datasources?: Array<{
      label: string;
      value: { name: string; id: string; datasourceId: string; datasourceClass: string };
    }>;
    isColumnSelect?: boolean;
    columnId?: string;
  }>;
  valueId?: string;
  newValue?: Value;
  searchData?: ItemFinderSearchData;
  propertyValues?: string[];
  ancestorPredicateId?: string;
  level?: number;
};

interface ItemFinderSearchData {
  searchType?: SearchType;
  folderId?: string;
  types?: string[];
  ancestorId?: string;
  itemIds?: string[];
  isInclude?: boolean;
  isCollapsed?: boolean;
  hierarchyId?: string;
  predicates?: Property[];
  ancestorPredicates?: AncestorPredicate[];
  storedTableId?: string;
  storedTableColumns?: ReactSelectOption[];
  isSimpleSearch?: boolean;
  hierarchyTableProperties?: SearchPropertyOption[];
  hierarchyLevelColumns?: HierarchyLevelColumn[];
  level?: number;
  hierarchySearchMode?: HierarchySearchMode;
  hierarchyPaths?: HierarchyPath[];
  searchItemTable?: SelectedSearchItem;
  searchHierarchy?: SelectedSearchItem;
  scalingTable?: SelectedSearchItem;
  scalingColumn?: SelectedSearchItem;
  scalingTableColumns?: ColumnDefinition[];
  tableDefinitionId?: string;
  columnDefinitionId?: string;
  pathOverride?: boolean;
  assetLeafChildFilter?: string;
  selectedChildNames?: string[];
  selectAllChildren?: boolean;
  allFilteredChildrenSelected?: boolean;
}

export type HierarchyPath = {
  nodes: HierarchyPathNode[];
};

export type HierarchyPathNode = {
  columnId: string;
  value: string | undefined;
};

export type SearchObject = {
  searchId: string;
  searchType: SearchType;
  isCollapsed: boolean;
  itemIds?: string[];
  predicates?: Property[];
  storedTableId?: string;
  storedTableColumns?: ReactSelectOption[];
  types?: string[];
  isSimpleSearch?: boolean;
  ancestorPredicates?: AncestorPredicate[];
  hierarchyId?: string;
  hierarchyTableProperties?: SearchPropertyOption[];
  hierarchyLevelColumns?: HierarchyLevelColumn[];
  level?: number;
  hierarchyPaths?: HierarchyPath[];
  searchItemTable?: SelectedSearchItem;
  searchHierarchy?: SelectedSearchItem;
  scalingTable?: SelectedSearchItem;
  scalingColumn?: SelectedSearchItem;
  scalingTableColumns?: ColumnDefinition[];
  tableDefinitionId?: string;
  columnDefinitionId?: string;
  fixedListPredicates?: FixedListFilterInputV1[];
  hierarchySearchMode?: HierarchySearchMode;
  ancestorId?: string;
  assetLeafChildFilter?: string;
  selectedChildNames?: string[];
  selectAllChildren?: boolean;
  allFilteredChildrenSelected?: boolean;
};

export const INITIAL_SEARCHES: SearchObject[] = [
  {
    ...DEFAULT_FIXED_LIST_SEARCH,
    searchId: base64guid(),
    isCollapsed: false,
    predicates: [],
  },
];

export type DatasourceLabel = {
  label: string;
  value: {
    id: string;
    name: string;
    datasourceId: string;
    datasourceClass: string;
  };
};

export type ExistingItemFinderValue = {
  id: string;
  name: string;
  searches: SearchObject[];
  cronSchedule: string[];
  timezone?: string;
  loadedSearchesStorePolicy: LoadedSearchesStorePolicy;
};

type ItemFinderState = {
  id: string;
  name: string;
  searches: SearchObject[];
  cronSchedule: string[];
  timezone?: string;
  storedTablesAvailable: ItemPreviewV1[];
  /** One token per in-flight SET_EXISTING_ITEM_FINDER hydration; cleared on reset so stale loads cannot clear it. */
  pendingSearchLoadIds: number[];
};

const uniqueHandlingProperties: string[] = [
  SeeqNames.Properties.DatasourceId,
  SeeqNames.Properties.DatasourceClass,
  SeeqNames.Properties.ScopedTo,
];

function hydratePredicates(predicates: PropertyFilterInputV1[] | undefined, searchId: string) {
  return (predicates ?? []).map((predicate) => {
    const propertyId = base64guid();
    const values = (predicate.values || []).map((value) => ({
      ...value,
      valueId: base64guid(),
      propertyId,
      searchId,
    }));
    return { ...predicate, propertyId, searchId, values };
  });
}

function duplicateSearchWithNewIds(search: Search, searchId: string): Search & { isCollapsed: boolean } {
  const duplicate = _.cloneDeep(search);
  const duplicatePredicates = (predicates: Property[] | undefined): Property[] | undefined =>
    predicates?.map((predicate) => {
      const propertyId = base64guid();
      return {
        ...predicate,
        propertyId,
        searchId,
        values: (predicate.values ?? []).map((value) => ({
          ...value,
          valueId: base64guid(),
          propertyId,
          searchId,
        })),
      };
    });

  return {
    ...duplicate,
    searchId,
    isCollapsed: false,
    predicates: duplicatePredicates(duplicate.predicates),
    ancestorPredicates: duplicate.ancestorPredicates?.map((ancestorPredicate) => ({
      ...ancestorPredicate,
      ancestorPredicateId: base64guid(),
      predicates: duplicatePredicates(ancestorPredicate.predicates) ?? [],
    })),
  };
}

export class ItemFinderStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  static readonly storeName = 'sqItemFinderStore';
  private nextSearchLoadId = 0;

  initialize(): void {
    const initialState: ItemFinderState = {
      id: '',
      name: '',
      searches: INITIAL_SEARCHES,
      cronSchedule: [],
      timezone: undefined,
      storedTablesAvailable: [],
      pendingSearchLoadIds: [],
    };

    this.state = this.immutable(initialState);
  }

  get id(): string {
    return this.state.get('id');
  }

  get name(): string {
    return this.state.get('name');
  }

  get searches(): Search[] {
    return this.getSearches(this.state.get('searches'));
  }

  get cronSchedule(): string[] {
    return this.state.get('cronSchedule');
  }

  get timezone(): string | undefined {
    return this.state.get('timezone');
  }

  get storedTablesAvailable(): ItemPreviewV1[] {
    return this.state.get('storedTablesAvailable');
  }

  /** True while an existing item finder's searches are being hydrated and the stored searches are not yet usable. */
  get isLoadingSearches(): boolean {
    return this.state.get('pendingSearchLoadIds').length > 0;
  }

  private getSearches: (searches: Search[]) => Search[] = createSelector(
    (searches: Search[]) => searches,
    (searches) => searches.map((search, index) => ({ ...search, searchId: search.searchId ?? index })),
  );

  protected readonly handlers = {
    RESET_ITEM_FINDER: (): void => {
      this.initialize();
    },
    SET_EXISTING_ITEM_FINDER: ({ value }: { value: ExistingItemFinderValue }): void => {
      async function initializePredicates(searchId: string, predicates: Property[]): Promise<Property[]> {
        return await Promise.all(
          predicates.map(async (property) => {
            property.propertyId = property.propertyId ?? base64guid();
            property.searchId = searchId;

            if (Array.isArray(property.values)) {
              property.values = property.values.map((val) => {
                val.valueId = val.valueId ?? base64guid();
                val.propertyId = property.propertyId;
                val.searchId = searchId;
                if (
                  property.propertyName === SeeqNames.Properties.ScopedTo &&
                  property.operator === OperatorEnum.WITHIN &&
                  _.isEmpty(val.value)
                ) {
                  val.value = SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE;
                }
                return val;
              });
            }

            if (property.propertyName === SeeqNames.Properties.DatasourceClass) {
              const datasourceIdProperty = predicates.find(
                (candidate) => candidate.propertyName === SeeqNames.Properties.DatasourceId,
              );

              const datasourcePromises = datasourceIdProperty?.values?.length
                ? datasourceIdProperty.values.map((idValue) =>
                    findDatasourceNamesAndIds(undefined, idValue.value as string),
                  )
                : property.values.map((classValue) => findDatasourceNamesAndIds(classValue.value as string));

              const resolvedDatasources = await Promise.all(datasourcePromises);
              property.datasources = resolvedDatasources
                .filter((response): response is NonNullable<typeof response> => response !== undefined)
                .map((response) => ({
                  label: response.name,
                  value: {
                    name: response.name,
                    id: response.id,
                    datasourceId: response.datasourceId ?? '',
                    datasourceClass: response.datasourceClass ?? '',
                  },
                }));
            }

            if (property.columnId !== undefined) {
              property.isColumnSelect = true;
              property.values = [{ value: '', propertyId: property.propertyId, searchId, valueId: base64guid() }];
            } else {
              property.isColumnSelect = false;
            }

            return property;
          }),
        );
      }

      function initializeHierarchyPaths(fixedListPredicates: FixedListFilterInputV1[]): HierarchyPath[] {
        return fixedListPredicates.map((predicate) => ({
          nodes: predicate.nodePaths.map((nodePath) => ({
            columnId: nodePath.columnId,
            value: nodePath.value?.value as string | undefined,
          })),
        }));
      }

      const updateSearchesAndDatasources = async (value: ExistingItemFinderValue) => {
        // Hydrations can overlap, so each one only clears its own token. A reset drops every token, which makes
        // completions of loads started before it no-ops for the loading state.
        const loadId = this.nextSearchLoadId++;
        this.state.push('pendingSearchLoadIds', loadId);
        try {
          await commitHydratedSearches(value);
        } finally {
          const index = this.state.get('pendingSearchLoadIds').indexOf(loadId);
          if (index > -1) {
            this.state.splice('pendingSearchLoadIds', [index, 1]);
          }
        }
      };

      const commitHydratedSearches = async (value: ExistingItemFinderValue) => {
        const updatedSearches = await Promise.all(
          value.searches.map(async (search) => {
            search.searchId = search.searchId ?? base64guid();

            if (Array.isArray(search.predicates)) {
              search.predicates = await initializePredicates(search.searchId, search.predicates);
            }

            if (Array.isArray(search.fixedListPredicates)) {
              search.hierarchyPaths = initializeHierarchyPaths(search.fixedListPredicates);
            }

            if (Array.isArray(search.ancestorPredicates)) {
              search.ancestorPredicates = await Promise.all(
                search.ancestorPredicates.map(async (ancestorPredicate) => {
                  ancestorPredicate.predicates = await initializePredicates(
                    search.searchId,
                    ancestorPredicate.predicates,
                  );
                  ancestorPredicate.ancestorPredicateId = ancestorPredicate.ancestorPredicateId ?? base64guid();
                  return ancestorPredicate;
                }),
              );
            }

            if (
              search.searchType === SearchTypeEnum.STOREDTABLE &&
              search.storedTableId &&
              search.storedTableColumns === undefined
            ) {
              search.storedTableColumns = await loadStoredTableColumns(search.storedTableId);
            }

            if (search.searchType === SearchTypeEnum.HIERARCHY && search.hierarchyId) {
              const searchOptions = await loadHierarchySearchOptions(search.hierarchyId);
              search.hierarchyTableProperties = searchOptions.hierarchyTableProperties;
              search.hierarchyLevelColumns = searchOptions.hierarchyLevelColumns;
              search.searchItemTable = searchOptions.itemTable;
              search.searchHierarchy = searchOptions.hierarchy;
              if (search.fixedListPredicates?.length) {
                search.hierarchySearchMode = HierarchySearchMode.FIXED_LIST;
              } else if (search.predicates?.length || search.ancestorPredicates?.length) {
                search.hierarchySearchMode = HierarchySearchMode.PROPERTY;
              } else {
                search.hierarchySearchMode = HierarchySearchMode.SIMPLE;
              }
            }

            if (search.searchType === SearchTypeEnum.STOREDTABLE) {
              search.isSimpleSearch = isStoredTableSimpleSearch(search);
            }

            return search;
          }),
        );

        const existingSearches = value.id === this.state.get('id') ? (this.state.get('searches') as Search[]) : [];
        const combinedSearches = [...updatedSearches, ...existingSearches].filter((search) => search.searchType);

        this.state.set('id', value.id);
        this.state.set('name', value.name);
        this.state.set(
          'searches',
          value.loadedSearchesStorePolicy === 'mergeWithCurrent' ? combinedSearches : updatedSearches,
        );
        this.state.set('cronSchedule', value.cronSchedule);
        this.state.set('timezone', value.timezone);
      };

      updateSearchesAndDatasources(value);
    },
    ITEM_FINDER_SET_NAME: ({ name }: { name: string }): void => {
      this.state.set('name', name);
    },
    ITEM_FINDER_ADD_SEARCH: (): void => {
      const searchId = base64guid();
      const newSearch = { ...DEFAULT_FIXED_LIST_SEARCH, searchId, isCollapsed: false };
      this.state.push('searches', newSearch);
    },
    ITEM_FINDER_REMOVE_SEARCH: (index: number): void => {
      if (index > -1) {
        this.state.splice('searches', [index, 1]);
      }
    },
    ITEM_FINDER_DUPLICATE_SEARCH: ({
      searchId,
      duplicateSearchId,
    }: {
      searchId: string | number;
      duplicateSearchId: string;
    }): void => {
      const searches = this.state.get('searches') as Search[];
      const sourceIndex = searches.findIndex((search, index) => (search.searchId ?? index) === searchId);
      if (sourceIndex < 0) {
        return;
      }

      const source = searches[sourceIndex];
      const duplicate = duplicateSearchWithNewIds(source, duplicateSearchId);
      const reorderedSearches = [
        ...searches.slice(0, sourceIndex + 1),
        duplicate,
        ...searches.slice(sourceIndex + 1),
      ].map((search, index) => ({
        ...search,
        searchOrder: index + 1,
      }));

      this.state.set('searches', reorderedSearches);
    },
    ITEM_FINDER_SET_CRON_SCHEDULE: ({ cronSchedule }: { cronSchedule: string[] }): void => {
      this.state.set('cronSchedule', cronSchedule);
    },
    ITEM_FINDER_SET_TIMEZONE: ({ timezone }: { timezone: string | undefined }): void => {
      this.state.set('timezone', timezone);
    },
    ITEM_FINDER_SET_STORED_TABLES_AVAILABLE: ({
      storedTablesAvailable,
    }: {
      storedTablesAvailable: ItemPreviewV1[];
    }) => {
      this.state.set('storedTablesAvailable', storedTablesAvailable);
    },
    ITEM_FINDER_UPDATE_SEARCH: (payload: UpdateSearchPayload): void => {
      const { action, index, searchId, searchData, propertyData, newValue, propertyValues } = payload;

      const searches = this.state.get('searches') as Search[];
      const searchIndex = searchId !== undefined ? searches.findIndex((s) => s.searchId === searchId) : index;
      if (searchIndex === undefined) {
        return;
      }

      const currentSearch = searches[searchIndex];
      let updatedSearch = { ...currentSearch };

      if (currentSearch) {
        switch (action) {
          case 'UPDATE_SEARCH_FIELDS': {
            updatedSearch = {
              ...currentSearch,
              ...searchData,
            } as Search;
            break;
          }

          case 'ADD_PROPERTY': {
            const propertyId = base64guid();
            const values: Value[] = [];
            if (propertyValues) {
              propertyValues.forEach((value) => {
                values.push({
                  value,
                  propertyId,
                  searchId: searchId as string,
                  valueId: base64guid(),
                });
              });
            }
            const newProperty: Property = {
              propertyId,
              searchId: searchId as string,
              propertyName: propertyData?.propertyName as string,
              operator: propertyData?.operator as OperatorEnum,
              values: propertyValues
                ? values
                : [{ value: '', propertyId, searchId: searchId as string, valueId: base64guid() }],
              isColumnSelect:
                updatedSearch.searchType === SearchTypeEnum.STOREDTABLE &&
                !uniqueHandlingProperties.includes(propertyData?.propertyName ?? ''),
            };

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: [...(currentSearch.predicates || []), newProperty],
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: [...ap.predicates, newProperty],
                    };
                  }
                  return ap;
                }),
              };
            }
            break;
          }

          case 'REMOVE_PROPERTY': {
            if (!payload.propertyId) {
              break;
            }

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: (currentSearch.predicates || []).filter(
                  (p: { propertyId: string }) => p.propertyId !== payload.propertyId,
                ),
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: ap.predicates.filter(
                        (p: { propertyId: string }) => p.propertyId !== payload.propertyId,
                      ),
                    };
                  }
                  return ap;
                }),
              };
            }
            break;
          }

          case 'UPDATE_PROPERTY': {
            if (!payload.propertyId) {
              break;
            }

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: (currentSearch.predicates || []).map((p) =>
                  p.propertyId === payload.propertyId
                    ? {
                        ...p,
                        ...propertyData,
                      }
                    : p,
                ),
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: (ap.predicates || []).map((p) =>
                        p.propertyId === payload.propertyId
                          ? {
                              ...p,
                              ...propertyData,
                            }
                          : p,
                      ),
                    };
                  }
                  return ap;
                }),
              };
            }

            break;
          }

          case 'ADD_VALUE': {
            if (!payload.propertyId) break;
            const newValueId = base64guid();
            const updatedValue: Value = {
              value: '',
              propertyId: payload.propertyId,
              searchId: searchId as string,
              valueId: newValueId,
            };

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: (currentSearch.predicates || []).map((p) =>
                  p.propertyId === payload.propertyId
                    ? {
                        ...p,
                        values: [...p.values, updatedValue],
                      }
                    : p,
                ),
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: (ap.predicates || []).map((p) =>
                        p.propertyId === payload.propertyId
                          ? {
                              ...p,
                              values: [...p.values, updatedValue],
                            }
                          : p,
                      ),
                    };
                  }
                  return ap;
                }),
              };
            }

            break;
          }

          case 'REMOVE_VALUE': {
            const { propertyId, valueId } = payload;
            if (!propertyId || !valueId) break;

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: (currentSearch.predicates || []).map((p) => {
                  if (p.propertyId === propertyId) {
                    const updatedValues = p.values.filter((v) => v.valueId !== valueId);
                    return { ...p, values: updatedValues };
                  }
                  return p;
                }),
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: (ap.predicates || []).map((p) => {
                        if (p.propertyId === propertyId) {
                          const updatedValues = p.values.filter((v) => v.valueId !== valueId);
                          return { ...p, values: updatedValues };
                        }
                        return p;
                      }),
                    };
                  }
                  return ap;
                }),
              };
            }

            break;
          }

          case 'UPDATE_VALUE': {
            if (!payload.propertyId || !payload.valueId) break;

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: (currentSearch.predicates || []).map((p) => {
                  if (p.propertyId === payload.propertyId) {
                    const updatedValues = p.values.map((v) =>
                      v.valueId === payload.valueId ? { ...v, ...newValue } : v,
                    );
                    return { ...p, values: updatedValues };
                  }
                  return p;
                }),
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: (ap.predicates || []).map((p) => {
                        if (p.propertyId === payload.propertyId) {
                          const updatedValues = p.values.map((v) =>
                            v.valueId === payload.valueId ? { ...v, ...newValue } : v,
                          );
                          return { ...p, values: updatedValues };
                        }
                        return p;
                      }),
                    };
                  }
                  return ap;
                }),
              };
            }
            break;
          }

          case 'UPDATE_STORED_TABLE_COLUMN': {
            if (!payload.propertyId || !payload.propertyData?.columnId) break;

            if (!payload.ancestorPredicateId) {
              updatedSearch = {
                ...currentSearch,
                predicates: (currentSearch.predicates || []).map((p) => {
                  if (p.propertyId === payload.propertyId) {
                    return { ...p, columnId: payload.propertyData?.columnId };
                  }
                  return p;
                }),
              };
            } else {
              updatedSearch = {
                ...currentSearch,
                ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                  if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                    return {
                      ...ap,
                      predicates: (ap.predicates || []).map((p) => {
                        if (p.propertyId === payload.propertyId) {
                          return { ...p, columnId: payload.propertyData?.columnId };
                        }
                        return p;
                      }),
                    };
                  }
                  return ap;
                }),
              };
            }
            break;
          }

          case 'ADD_DEFAULT_STORED_TABLE_SEARCH': {
            updatedSearch = {
              ...currentSearch,
              types: [...DEFAULT_STORED_TABLE_SIMPLE_SEARCH_TYPES],
              predicates: [createDefaultStoredTableNameProperty(searchId as string)],
              ancestorPredicates: [],
              isSimpleSearch: true,
            };

            break;
          }

          case 'ADD_ANCESTOR_PREDICATE': {
            const propertyId = base64guid();
            const valueId = base64guid();
            const ancestorPredicateId = base64guid();
            const values: Value[] = [];
            if (propertyValues) {
              propertyValues.forEach((value) => {
                values.push({
                  value,
                  propertyId,
                  searchId: searchId as string,
                  valueId,
                });
              });
            }
            const newProperty: Property = {
              propertyId,
              searchId: searchId as string,
              propertyName: propertyData?.propertyName as string,
              operator: propertyData?.operator as OperatorEnum,
              values: propertyValues ? values : [{ value: '', propertyId, searchId: searchId as string, valueId }],
              isColumnSelect: updatedSearch.searchType === SearchTypeEnum.STOREDTABLE,
            };

            const newAncestorPredicate: AncestorPredicate = {
              ancestorPredicateId,
              predicates: [newProperty],
            };

            updatedSearch = {
              ...currentSearch,
              ancestorPredicates: [...(currentSearch.ancestorPredicates || []), newAncestorPredicate],
            };
            break;
          }

          case 'REMOVE_ANCESTOR_PREDICATE': {
            if (!payload.ancestorPredicateId) {
              break;
            }

            const updatedAncestorPredicates = (currentSearch.ancestorPredicates || []).filter(
              (ap) => ap.ancestorPredicateId !== payload.ancestorPredicateId,
            );
            updatedSearch = {
              ...currentSearch,
              ancestorPredicates: updatedAncestorPredicates,
            };

            break;
          }

          case 'UPDATE_ANCESTOR_PREDICATE': {
            if (!payload.ancestorPredicateId) {
              break;
            }

            updatedSearch = {
              ...currentSearch,
              ancestorPredicates: (currentSearch.ancestorPredicates || []).map((ap) => {
                if (ap.ancestorPredicateId === payload.ancestorPredicateId) {
                  return {
                    ...ap,
                    predicates: (ap.predicates || []).map((p) =>
                      p.propertyId === payload.propertyId
                        ? {
                            ...p,
                            ...propertyData,
                          }
                        : p,
                    ),
                    level: payload.level,
                  };
                }
                return ap;
              }),
            };

            break;
          }

          default:
            return;
        }
      }

      this.state.splice('searches', [searchIndex, 1, updatedSearch]);
    },
    ITEM_FINDER_ADD_PROPERTY_SEARCH: (propertySearch: SearchObject): void => {
      if (propertySearch.searchType !== SearchTypeEnum.PROPERTY) {
        return;
      }

      const searchId = base64guid();
      const newSearch = {
        ...propertySearch,
        searchId,
      };

      const updatedSearches = [...(this.state.get('searches') || []), newSearch].filter((search) => search.searchType);
      this.state.set('searches', updatedSearches);

      const updatedPredicates = hydratePredicates(propertySearch.predicates, searchId);

      const updatedSearchWithPredicates = { ...newSearch, predicates: updatedPredicates };

      this.state.set(
        'searches',
        updatedSearches.map((search) =>
          search.searchId === newSearch.searchId ? updatedSearchWithPredicates : search,
        ),
      );
    },
    ITEM_FINDER_ADD_FIXED_LIST_SEARCH: (fixedListSearch: SearchObject): void => {
      if (fixedListSearch.searchType !== SearchTypeEnum.FIXEDLIST) {
        return;
      }

      const searchId = base64guid();
      const newSearch = {
        ...fixedListSearch,
        searchId,
      };
      const combinedSearches = [...(this.state.get('searches') || []), newSearch].filter((search) => search.searchType);
      this.state.set('searches', combinedSearches);
    },
    ITEM_FINDER_REPLACE_SEARCH: ({ index, search }: { index: number; search: SearchObject }): void => {
      const searches = this.state.get('searches') as Search[];
      const current = searches[index];
      if (index < 0 || !current) {
        return;
      }

      const searchId = typeof current.searchId === 'string' ? current.searchId : base64guid();
      const replacement = {
        ...search,
        searchId,
        searchOrder: current.searchOrder,
        isCollapsed: false,
      } as SearchObject;

      if (replacement.searchType === SearchTypeEnum.PROPERTY) {
        replacement.predicates = hydratePredicates(replacement.predicates, searchId);
      }

      this.state.splice('searches', [index, 1, replacement]);
    },
    ITEM_FINDER_REORDER_SEARCHES: ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void => {
      const searches = [...(this.state.get('searches') as Search[])];

      if (fromIndex < 0 || fromIndex >= searches.length || toIndex < 0 || toIndex >= searches.length) {
        return;
      }

      const [movedSearch] = searches.splice(fromIndex, 1);

      searches.splice(toIndex, 0, movedSearch);

      const reorderedSearches = searches.map((search, index) => ({
        ...search,
        searchOrder: index + 1,
      }));

      this.state.set('searches', reorderedSearches);
    },
  };
}
